'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuthUser } from '@/hooks/useAuthUser';
import organizationsService from '@/services/organizations-service';
import type { OrgGroup, OrgMember } from '@/services/organizations-service';
import sessionServices, {
  type SessionShareGrant,
} from '@/services/session-services';
import { Loader2, Share2, User, Users } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ShareSessionModalProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function getMemberDisplayName(member: OrgMember): string {
  const parts = [member.first_name, member.last_name].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0
  );
  if (parts.length > 0) return parts.join(' ');
  if (member.identifier) return member.identifier;
  return '—';
}

function getMemberSubtitle(member: OrgMember): string | null {
  const hasName =
    typeof member.first_name === 'string' ||
    typeof member.last_name === 'string';
  if (hasName && member.identifier) return member.identifier;
  return null;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function SectionHeading({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className='flex items-center gap-2 px-1 pb-1 pt-3'>
      <span className='text-content-muted'>{icon}</span>
      <p className='text-xs font-semibold uppercase tracking-wide text-content-muted'>
        {label}
      </p>
    </div>
  );
}

function EmptySection({ message }: { message: string }) {
  return <p className='px-1 py-2 text-sm text-content-muted'>{message}</p>;
}

function RowLoader() {
  return (
    <Loader2
      className='h-4 w-4 animate-spin text-content-muted shrink-0'
      aria-hidden='true'
    />
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export function ShareSessionModal({
  sessionId,
  isOpen,
  onClose,
}: ShareSessionModalProps) {
  const t = useTranslations('Organizations');
  const { orgId, userId } = useAuthUser();
  const { toast } = useToast();

  const [grants, setGrants] = useState<SessionShareGrant[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [orgGroups, setOrgGroups] = useState<OrgGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  // ─── data loading ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!orgId) return;

    setIsLoading(true);
    try {
      const [sharesRes, membersRes, groupsRes] = await Promise.all([
        sessionServices.getSessionShares(sessionId),
        organizationsService.getOrgMembers(orgId),
        organizationsService.getOrgGroups(orgId),
      ]);

      if (sharesRes.status === 'success' && sharesRes.data) {
        setGrants(sharesRes.data.grants);
      }

      if (membersRes.status === 'success' && membersRes.data) {
        setOrgMembers(membersRes.data.members);
      }

      if (groupsRes.status === 'success' && groupsRes.data) {
        setOrgGroups(groupsRes.data);
      }
    } catch {
      toast({
        title: t('shareLoadError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [orgId, sessionId, t, toast]);

  useEffect(() => {
    if (isOpen) {
      void loadData();
    } else {
      // Reset state when modal closes
      setGrants([]);
      setOrgMembers([]);
      setOrgGroups([]);
      setPendingIds(new Set());
    }
  }, [isOpen, loadData]);

  // ─── toggle helpers ────────────────────────────────────────────────────────

  function isGranted(
    granteeType: 'user' | 'group',
    granteeId: string
  ): boolean {
    return grants.some(
      (g) => g.grantee_type === granteeType && g.grantee_id === granteeId
    );
  }

  async function handleToggle(
    granteeType: 'user' | 'group',
    granteeId: string,
    currentlyGranted: boolean
  ) {
    const key = `${granteeType}:${granteeId}`;
    setPendingIds((prev) => new Set(prev).add(key));

    try {
      if (currentlyGranted) {
        const res = await sessionServices.removeSessionShare(
          sessionId,
          granteeType,
          granteeId
        );
        if (res.status === 'success') {
          setGrants((prev) =>
            prev.filter(
              (g) =>
                !(g.grantee_type === granteeType && g.grantee_id === granteeId)
            )
          );
        } else {
          toast({ title: t('shareUpdateError'), variant: 'destructive' });
        }
      } else {
        const res = await sessionServices.addSessionShare(
          sessionId,
          granteeType,
          granteeId
        );
        if (res.status === 'success' && res.data) {
          setGrants((prev) => [...prev, res.data]);
        } else {
          toast({ title: t('shareUpdateError'), variant: 'destructive' });
        }
      }
    } catch {
      toast({
        title: t('shareUpdateError'),
        variant: 'destructive',
      });
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  // ─── render ────────────────────────────────────────────────────────────────

  // Members visible in the list: exclude the current user (session owner)
  const visibleMembers = orgMembers.filter((m) => m.user_id !== userId);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='sm:max-w-md bg-surface-card border border-line-default'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-content-primary'>
            <Share2
              className='h-4 w-4 text-brand-tertiary shrink-0'
              aria-hidden='true'
            />
            {t('shareSession')}
          </DialogTitle>
          <DialogDescription className='text-sm text-content-secondary pt-1'>
            {t('shareSessionDesc')}
          </DialogDescription>
        </DialogHeader>

        {/* No org */}
        {!orgId && (
          <p className='py-4 text-sm text-content-muted text-center'>
            {t('shareNoOrg')}
          </p>
        )}

        {/* Loading skeleton */}
        {orgId && isLoading && (
          <div className='flex items-center justify-center py-8'>
            <Loader2
              className='h-5 w-5 animate-spin text-content-muted'
              aria-label={t('loadingMembers')}
            />
          </div>
        )}

        {/* Content */}
        {orgId && !isLoading && (
          <div className='space-y-1 overflow-y-auto max-h-[60vh]'>
            {/* Groups section */}
            <SectionHeading
              icon={<Users className='h-3.5 w-3.5' aria-hidden='true' />}
              label={t('shareGroups')}
            />

            {orgGroups.length === 0 ? (
              <EmptySection message={t('shareNoGroups')} />
            ) : (
              <ul className='divide-y divide-line-subtle rounded-md border border-line-subtle overflow-hidden'>
                {orgGroups.map((group) => {
                  const key = `group:${group.id}`;
                  const granted = isGranted('group', group.id);
                  const isPending = pendingIds.has(key);

                  return (
                    <li
                      key={group.id}
                      className='flex items-center gap-3 px-3 py-2.5 min-h-[44px]
                        hover:bg-surface-elevated transition-colors'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-content-primary truncate'>
                          {group.name}
                        </p>
                        <p className='text-xs text-content-muted'>
                          {group.member_count === 1
                            ? t('groupMemberSingular')
                            : t('groupMembers', { count: group.member_count })}
                        </p>
                      </div>

                      {/* stopPropagation prevents click events from bubbling
                          out of the portal into React ancestors (e.g. Link) */}
                      <div
                        className='shrink-0 flex items-center gap-2 min-w-[44px] justify-end'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isPending ? (
                          <RowLoader />
                        ) : (
                          <Switch
                            checked={granted}
                            onCheckedChange={() =>
                              void handleToggle('group', group.id, granted)
                            }
                            aria-label={
                              granted
                                ? `${t('shareToggleOn')}: ${group.name}`
                                : `${t('shareToggleOff')}: ${group.name}`
                            }
                            disabled={isPending}
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Members section */}
            <SectionHeading
              icon={<User className='h-3.5 w-3.5' aria-hidden='true' />}
              label={t('shareMembers')}
            />

            {visibleMembers.length === 0 ? (
              <EmptySection message={t('shareNoMembers')} />
            ) : (
              <ul className='divide-y divide-line-subtle rounded-md border border-line-subtle overflow-hidden'>
                {visibleMembers.map((member) => {
                  const key = `user:${member.user_id}`;
                  const granted = isGranted('user', member.user_id);
                  const isPending = pendingIds.has(key);
                  const displayName = getMemberDisplayName(member);
                  const subtitle = getMemberSubtitle(member);
                  const normalizedRole = member.role.toLowerCase();
                  const isAdmin =
                    normalizedRole === 'owner' ||
                    normalizedRole === 'org:owner';

                  return (
                    <li
                      key={member.user_id}
                      className='flex items-center gap-3 px-3 py-2.5 min-h-[44px]
                        hover:bg-surface-elevated transition-colors'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-content-primary truncate'>
                          {displayName}
                        </p>
                        {subtitle && (
                          <p className='text-xs text-content-muted truncate'>
                            {subtitle}
                          </p>
                        )}
                      </div>

                      {/* Role badge */}
                      <span
                        className={
                          isAdmin
                            ? 'hidden sm:inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-brand-tertiary-muted text-brand-tertiary border border-brand-tertiary/20 shrink-0'
                            : 'hidden sm:inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-surface-muted text-content-secondary border border-line-subtle shrink-0'
                        }
                      >
                        {isAdmin ? t('roleAdmin') : t('roleMember')}
                      </span>

                      {/* stopPropagation prevents click events from bubbling
                          out of the portal into React ancestors (e.g. Link) */}
                      <div
                        className='shrink-0 flex items-center gap-2 min-w-[44px] justify-end'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isPending ? (
                          <RowLoader />
                        ) : (
                          <Switch
                            checked={granted}
                            onCheckedChange={() =>
                              void handleToggle('user', member.user_id, granted)
                            }
                            aria-label={
                              granted
                                ? `${t('shareToggleOn')}: ${displayName}`
                                : `${t('shareToggleOff')}: ${displayName}`
                            }
                            disabled={isPending}
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
